import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Button,
  Divider,
  Paper,
  IconButton
} from '@material-ui/core';
import {
  Close as CloseIcon,
  Sms as SmsIcon,
  EditAttributes as EditNoteIcon
} from '@material-ui/icons';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useMembersContext } from '../../context/onoff_context';
import { StatusPill, fullName } from './StatusPill';

const INTERACTION_TYPES = ['Call', 'Visit', 'SMS', 'Email', 'Office enquiry', 'Other'];

function Detail({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value && String(value).trim() ? value : '—'}
      </Typography>
    </Box>
  );
}

export function MemberDialog({ member, onClose, onSendSms }) {
  const {
    notes,
    sms,
    addNote: saveNote,
    savePhone: storePhone,
    fetchNotes,
    fetchSmsHistory
  } = useMembersContext();
  const [phone, setPhone] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [interaction, setInteraction] = useState(INTERACTION_TYPES[0]);
  const [saving, setSaving] = useState(false);
  console.log('member akele: ', member);

  useEffect(() => {
    setPhone(member?.phone ?? '');
    setNoteBody('');
    if (!member) return;
    fetchNotes(member).catch(() => toast.error('Previous notes could not be loaded.'));
    fetchSmsHistory(member).catch(() =>
      toast.error('Previous SMS communications could not be loaded.')
    );
  }, [member, fetchNotes, fetchSmsHistory]);

  const savePhone = {
    isPending: false,
    mutate: () => {
      storePhone(member, phone);
      toast.success('Phone number saved.');
    }
  };

  const addNote = {
    isPending: saving,
    mutate: async () => {
      setSaving(true);
      try {
        await saveNote(member, { title: interaction, content: noteBody.trim() });
        toast.success('Note added to the member profile.');
        setNoteBody('');
      } catch {
        toast.error('The note could not be saved.');
      } finally {
        setSaving(false);
      }
    }
  };

  const noteList = member
    ? (notes[member.id] ?? []).map((n) => ({ ...n, interaction_type: n.title, body: n.content }))
    : [];
  const smsList = member ? sms[member.id] ?? [] : [];

  return (
    <Dialog open={Boolean(member)} onClose={onClose} fullWidth maxWidth="md">
      {member && (
        <>
          <DialogTitle sx={{ pr: 6 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Typography variant="h5">{fullName(member)}</Typography>
              <StatusPill
                tone={member.on_off === 'On' ? 'on' : 'off'}
              >{`${member.on_off} pay`}</StatusPill>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Employee {member.emp_no} · {member.status || 'No status recorded'}
            </Typography>
            <IconButton
              onClick={onClose}
              sx={{ position: 'absolute', right: 12, top: 12 }}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
                gap: 2,
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 2
              }}
            >
              <Detail label="Station" value={member.station_description} />
              <Detail label="Department" value={member.department} />
              <Detail label="Subsection" value={member.subsection} />
              <Detail label="Area" value={member.area_desc} />
              <Detail label="Area code" value={member.area_code} />
              <Detail label="Gender" value={member.gender} />
              <Detail label="National reg. no." value={member.nat_reg_no} />
              <Detail label="Station code" value={member.stat} />
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ my: 3 }}
              alignItems="center"
            >
              <TextField
                label="Phone number"
                size="small"
                fullWidth
                placeholder="+263 77 000 0000"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <Button
                variant="outlined"
                disabled={savePhone.isPending}
                onClick={() => savePhone.mutate()}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0, px: 2 }}
              >
                Save number
              </Button>
              <Button
                variant="contained"
                startIcon={<SmsIcon />}
                onClick={() => onSendSms(member)}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0, px: 2 }}
              >
                Send SMS
              </Button>
            </Stack>

            <Divider />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 3,
                mt: 3
              }}
            >
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EditNoteIcon color="secondary" /> Record an interaction
                </Typography>
                <TextField
                  select
                  label="Interaction type"
                  size="small"
                  value={interaction}
                  onChange={(e) => setInteraction(e.target.value)}
                >
                  {INTERACTION_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Note"
                  multiline
                  rows={4}
                  placeholder="What was discussed and what happens next?"
                  value={noteBody}
                  onChange={(e) => setNoteBody(e.target.value)}
                />
                <Button
                  variant="contained"
                  disabled={noteBody.trim().length === 0 || addNote.isPending}
                  onClick={() => addNote.mutate()}
                >
                  Add note to profile
                </Button>
              </Stack>

              <Stack spacing={2}>
                <Typography variant="h6">Profile history</Typography>
                <Paper variant="outlined" sx={{ height: 290, overflowY: 'auto', p: 1.5 }}>
                  {noteList.length === 0 && smsList.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No interactions recorded for this member yet.
                    </Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      {noteList.map((note) => (
                        <Box
                          key={note.id}
                          sx={{ bgcolor: 'background.default', p: 1.5, borderRadius: 1 }}
                        >
                          <Typography
                            variant="caption"
                            color="secondary"
                            fontWeight={700}
                            sx={{ textTransform: 'uppercase' }}
                          >
                            {note.interaction_type}
                          </Typography>
                          <Typography variant="body2">{note.body}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(note.created_at).toLocaleString()}
                          </Typography>
                        </Box>
                      ))}
                      {smsList.map((m) => (
                        <Box
                          key={m.id}
                          sx={{
                            border: '1px dashed',
                            borderColor: 'divider',
                            p: 1.5,
                            borderRadius: 1
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={700}
                            sx={{ textTransform: 'uppercase' }}
                          >
                            SMS · {m.delivery_status}
                          </Typography>
                          <Typography variant="body2">{m.body}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(m.created_at).toLocaleString()}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Paper>
              </Stack>
            </Box>
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}
